import { z } from 'zod';
import { contentIdSchema } from './content-id.js';
/**
 * Контекст расчёта (rules-engine.md, раздел 4): уровень
 * и характеристики персонажа плюс состояние из событий кампании.
 *
 * Состав контекста фиксирован схемой. Путь, которого в ней нет, —
 * ошибка расчёта, а не `undefined`: молчаливое «нет значения»
 * превращается в неверный лист, которого никто не заметит.
 */
export const abilityKeySchema = z.enum(['str', 'dex', 'con', 'int', 'wis', 'cha']);
export const abilityScoreSchema = z.strictObject({
    score: z.number().int(),
    /** Модификатор считается при сборке контекста, а не в движке. */
    mod: z.number().int(),
});
/**
 * Состояние кампании, от которого зависят условия: надетое, текущая
 * концентрация (D3 — состояние живёт в событиях кампании, а не в полях
 * персонажа) и активные эффекты.
 */
export const campaignStateSchema = z.strictObject({
    equipped: z.array(contentIdSchema),
    concentration: contentIdSchema.nullable(),
    activeEffects: z.array(contentIdSchema),
});
/**
 * Груз: сколько персонаж несёт и сколько может нести.
 *
 * В контексте он по той же причине, что и уровень: правило источника
 * ссылается на него, а не выводит его. «While dragging, lifting,
 * or pushing weight in excess of the maximum weight you can carry,
 * your Speed can be no more than 5 feet» (SRD 5.2, Rules Glossary,
 * Carrying Capacity, с. 178) — это условие на вес, и записать его
 * иначе как условием по контексту нельзя.
 *
 * Считается груз не движком: вес складывается из инвентаря, а предел —
 * из таблицы записи правил по Силе и размеру. Движку он приходит
 * готовым, как и характеристики.
 */
export const carryStateSchema = z.strictObject({
    /** Вес всего, что при себе, включая надетое. */
    weightLb: z.number().nonnegative(),
    /** Предел переносимого: Сила × множитель размера. */
    capacityLb: z.number().nonnegative(),
    /**
     * Вес больше предела. Отдельным флагом, а не сравнением двух чисел
     * в условии: условие сравнивает путь с литералом, а не путь с путём
     * (rules-engine.md, раздел 4).
     */
    over: z.boolean(),
});
export const calcContextSchema = z.strictObject({
    /**
     * Уровень персонажа. В контексте он потому, что правила ссылаются
     * на него как на величину: «максимум хитов растёт на 1 за каждый
     * уровень» (Dwarven Toughness, с. 84). Лист — свёртка журнала (D4),
     * и уровень выводится из числа шагов, а не хранится; сюда он
     * приходит уже посчитанным — тем же числом, по которому отбираются
     * эффекты уровней класса.
     */
    level: z.number().int().positive(),
    abilities: z.strictObject({
        str: abilityScoreSchema,
        dex: abilityScoreSchema,
        con: abilityScoreSchema,
        int: abilityScoreSchema,
        wis: abilityScoreSchema,
        cha: abilityScoreSchema,
    }),
    state: campaignStateSchema,
    /** Груз: вес при себе и предел переносимого. */
    carry: carryStateSchema,
});
//# sourceMappingURL=context.js.map